import React from 'react';
import { motion } from 'framer-motion';
import { Award, Target, Users, Trophy, Heart, Globe, Lightbulb, Handshake, Building2, Star } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section with Parallax */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://titc.io/wp-content/uploads/2024/12/Screenshot-2024-10-18-at-08.48.34.png',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70" />
        </motion.div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6">
              Shaping the Future of
              <span className="block" style={{ color: '#C1B3A3' }}>
                African FinTech
              </span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 mb-8">
              More Than Just an Awards Ceremony - A Movement for Change
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold mb-6">Our Story</h2>
              <p className="text-lg text-gray-600">
                The SA FinTech Awards emerged from a vision shared by the team at <a href="https://www.titc.io" className="text-[#C1B3A3] hover:underline">titc.io</a> - to create a 
                platform that not only celebrates innovation but drives meaningful change in the 
                financial technology sector.
              </p>
              <p className="text-lg text-gray-600">
                As active participants and leaders in the FinTech ecosystem, we recognized the need 
                to spotlight the transformative work being done across South Africa, while fostering 
                collaboration and driving inclusive growth.
              </p>
              <div className="space-y-4 mt-8">
                {[
                  "Celebrating African innovation and excellence",
                  "Fostering collaboration across the ecosystem",
                  "Driving financial inclusion and accessibility",
                  "Supporting the next generation of FinTech leaders"
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Star className="h-5 w-5" style={{ color: '#C1B3A3' }} />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://titc.io/wp-content/uploads/2024/12/IMG_6700-scaled.jpg"
                alt="TITC Team"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* About TITC Section */}
      <section className="py-20" style={{ backgroundColor: 'rgba(193, 179, 163, 0.1)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">Meet Your Organizers</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              <a href="https://www.titc.io" className="text-[#C1B3A3] hover:underline">titc.io</a> is more than an event organizer - we're active participants and leaders in the 
              FinTech ecosystem, committed to driving innovation and inclusion
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: <Building2 className="h-8 w-8" />,
                title: "Industry Leaders",
                description: "Our team comprises seasoned FinTech professionals, each bringing decades of experience in financial technology, innovation, and digital transformation."
              },
              {
                icon: <Handshake className="h-8 w-8" />,
                title: "Community Builders",
                description: "We actively participate in and support various FinTech communities, fostering collaboration and knowledge sharing across the ecosystem."
              },
              {
                icon: <Heart className="h-8 w-8" />,
                title: "Change Makers",
                description: "Through our involvement with NPOs and charitable initiatives, we're committed to making financial services more accessible and inclusive."
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="mb-6" style={{ color: '#C1B3A3' }}>
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-8 lg:p-12">
                <h3 className="text-2xl font-bold mb-6">Our Commitment to Diversity & Inclusion</h3>
                <div className="space-y-6">
                  <p className="text-gray-600">
                    At <a href="https://www.titc.io" className="text-[#C1B3A3] hover:underline">titc.io</a>, we believe that true innovation comes from diverse perspectives and 
                    inclusive practices. Our team actively supports and participates in initiatives 
                    that promote:
                  </p>
                  <ul className="space-y-4">
                    <li className="flex items-start space-x-3">
                      <Globe className="h-6 w-6 mt-1" style={{ color: '#C1B3A3' }} />
                      <div>
                        <h4 className="font-semibold mb-1">Financial Inclusion</h4>
                        <p className="text-gray-600">
                          Working with NPOs to extend financial services to underserved communities
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Users className="h-6 w-6 mt-1" style={{ color: '#C1B3A3' }} />
                      <div>
                        <h4 className="font-semibold mb-1">Gender Equality</h4>
                        <p className="text-gray-600">
                          Supporting women in FinTech through mentorship and leadership programs
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Lightbulb className="h-6 w-6 mt-1" style={{ color: '#C1B3A3' }} />
                      <div>
                        <h4 className="font-semibold mb-1">Youth Development</h4>
                        <p className="text-gray-600">
                          Nurturing the next generation of FinTech innovators through education and opportunities
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative h-full min-h-[400px]">
                <img
                  src="https://titc.io/wp-content/uploads/2024/08/Bokkie.png"
                  alt="Diversity in FinTech"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6" style={{ color: '#C1B3A3' }}>Our Impact</h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Through our work and partnerships, we're helping shape a more inclusive and innovative 
              financial technology sector
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { number: "5000+", label: "FinTech Leaders Connected" },
              { number: "12", label: "NPO Partnerships" },
              { number: "30+", label: "Community Events" },
              { number: "56+", label: "Countries Covered" }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 rounded-xl"
                style={{ backgroundColor: 'rgba(193, 179, 163, 0.1)' }}
              >
                <div className="text-4xl font-bold mb-2" style={{ color: '#C1B3A3' }}>
                  {stat.number}
                </div>
                <div className="text-sm text-gray-300">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;